import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { SEO, getArticleSchema } from "../components/SEO";
import { apiFetch } from "../lib/api";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  coverImage: string | null;
  category: string;
  tags: string;
  publishedAt: string;
  readTime: number;
  seoTitle: string | null;
  seoDescription: string | null;
  views: number;
}

const categoryLabels: Record<string, string> = {
  novidades: "Novidades",
  cases: "Cases",
  dicas: "Dicas",
  tecnologia: "Tecnologia",
  mercado: "Mercado",
  tutoriais: "Tutoriais",
};

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    apiFetch(`/api/blog/${slug}`)
      .then((json) => {
        if (json.success) {
          setPost(json.data);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent-600 border-t-transparent" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center">
          <h1 className="text-4xl font-bold">404</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Artigo não encontrado.
          </p>
          <a href="/blog" className="mt-4 inline-block text-accent-600 hover:underline">
            ← Voltar para o Blog
          </a>
        </div>
      </div>
    );
  }

  const tags = post.tags ? JSON.parse(post.tags) : [];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO
        title={post.seoTitle || post.title}
        description={post.seoDescription || post.excerpt}
        keywords={tags}
        ogType="article"
        ogImage={post.coverImage || undefined}
        canonicalUrl={`/blog/${post.slug}`}
        structuredData={getArticleSchema({
          title: post.title,
          description: post.excerpt,
          publishedAt: post.publishedAt,
          image: post.coverImage || undefined,
          slug: post.slug,
        })}
      />

      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
        <div className="container flex h-16 items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-600 text-white font-bold">
              Z
            </div>
            <span className="text-xl font-bold">ZWeb</span>
          </a>
          <nav className="flex items-center gap-6">
            <a href="/" className="text-sm font-medium hover:text-accent-600">Home</a>
            <a href="/blog" className="text-sm font-medium text-accent-600">Blog</a>
            <a href="/solicitar-demo" className="text-sm font-medium hover:text-accent-600">Demo</a>
          </nav>
        </div>
      </header>

      <main className="container py-12">
        <article className="mx-auto max-w-3xl">
          <a href="/blog" className="text-sm text-accent-600 hover:underline">
            ← Voltar para o Blog
          </a>

          <div className="mt-6 flex items-center gap-3">
            <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800 dark:bg-accent-900 dark:text-accent-300">
              {categoryLabels[post.category] || post.category}
            </span>
            <span className="text-sm text-gray-500">{post.readTime} min de leitura</span>
            <span className="text-sm text-gray-500">{post.views} visualizações</span>
          </div>

          <h1 className="mt-4 text-4xl font-bold leading-tight">{post.title}</h1>

          {post.excerpt && (
            <p className="mt-4 text-xl text-gray-600 dark:text-gray-400">
              {post.excerpt}
            </p>
          )}

          <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
            <span>
              Publicado em{" "}
              {new Date(post.publishedAt).toLocaleDateString("pt-BR", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </span>
          </div>

          {post.coverImage && (
            <img
              src={post.coverImage}
              alt={post.title}
              className="mt-8 w-full rounded-2xl object-cover"
            />
          )}

          <div
            className="prose prose-lg mt-8 max-w-none dark:prose-invert prose-headings:font-bold prose-a:text-accent-600"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {tags.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2 border-t border-gray-200 pt-6 dark:border-gray-800">
              {tags.map((tag: string) => (
                <span
                  key={tag}
                  className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 rounded-2xl bg-gradient-to-br from-accent-500 to-violet-600 p-8 text-center text-white">
            <h3 className="text-2xl font-bold">Pronto para transformar sua empresa?</h3>
            <p className="mt-2 text-accent-100">
              Solicite uma demonstração gratuita do ZWeb hoje mesmo.
            </p>
            <a
              href="/solicitar-demo"
              className="mt-6 inline-block rounded-lg bg-white px-6 py-3 font-medium text-accent-600 hover:bg-accent-50"
            >
              Solicitar Demonstração
            </a>
          </div>
        </article>
      </main>

      {/* Footer simples */}
      <footer className="border-t border-gray-200 bg-white py-8 dark:border-gray-800 dark:bg-gray-950">
        <div className="container text-center text-sm text-gray-600 dark:text-gray-400">
          © 2026 ZWeb. Todos os direitos reservados.
        </div>
      </footer>
    </div>
  );
}
